import { isHttpError } from "~/utils/httpClient";
import {
  getApiResponseDiagnostics,
  InvalidApiResponseError,
} from "../utils/apiResponseDiagnostics";

const errorTypes = new Set([
  "Error",
  "TypeError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "HttpError",
  "FirebaseError",
  "SecurityError",
  "InvalidStateError",
  "QuotaExceededError",
  "AbortError",
  "NotAllowedError",
]);
const errorCodes = new Set([
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_CANCELED",
  "ERR_BAD_REQUEST",
  "ERR_BAD_RESPONSE",
  "auth/network-request-failed",
  "auth/user-token-expired",
  "auth/invalid-user-token",
  "auth/user-disabled",
  "auth/internal-error",
  "auth/too-many-requests",
  "auth/requires-recent-login",
  "auth/web-storage-unsupported",
]);

const getSourceStack = (error: Error) => {
  // Keep source-map locations, but omit messages, function names and URL secrets.
  const frames: string[] = [];
  for (const line of (error.stack ?? "").split("\n").slice(0, 20)) {
    const match = line.match(
      /^(?:\s*at (?:.*?\()?|[^@]*@|)(https?:\/\/[^\s)]+):(\d+):(\d+)\)?$/,
    );
    if (!match) continue;
    try {
      const url = new URL(match[1]!);
      if (
        url.origin !== window.location.origin ||
        !/^\/assets\/[\w.-]+\.js$/.test(url.pathname)
      )
        continue;
      frames.push(
        `    at ${url.origin}${url.pathname}:${match[2]}:${match[3]}`,
      );
    } catch {
      // Ignore frames that cannot be safely associated with our frontend assets.
    }
  }
  return frames.length
    ? ["Error: Session initialization failed", ...frames].join("\n")
    : undefined;
};

/** Construct a new allowlisted snapshot; never send the caught exception itself. */
export const getSessionErrorDiagnostics = (error: unknown) => {
  try {
    const errorType =
      error instanceof Error && errorTypes.has(error.name)
        ? error.name
        : "other";
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? error.code
        : undefined;
    let reason = "unclassified";
    if (isHttpError(error) && error.reason === "invalid-json")
      reason = "invalid-json";
    else if (error instanceof InvalidApiResponseError)
      reason = "invalid-response-envelope";
    else if (error instanceof Error) {
      switch (error.message) {
        case "Invalid session response":
          reason = "invalid-session-payload";
          break;
        case "Profile provisioning did not complete":
          reason = "profile-provisioning-incomplete";
          break;
        case "Missing authentication email":
          reason = "missing-authentication-email";
          break;
      }
      if (error.message.endsWith(" data is undefined"))
        reason = "undefined-query-data";
    }
    return {
      errorType,
      code:
        typeof code === "string" && errorCodes.has(code)
          ? code
          : code === undefined
            ? "absent"
            : "other",
      reason,
      response:
        error instanceof InvalidApiResponseError
          ? error.diagnostics
          : isHttpError(error) && error.response
            ? (error.diagnostics ?? getApiResponseDiagnostics(error.response))
            : undefined,
      sourceStack: error instanceof Error ? getSourceStack(error) : undefined,
    };
  } catch {
    return {
      errorType: "unavailable",
      code: "other",
      reason: "diagnostics-unavailable",
    };
  }
};
